import { memo, useEffect, useState } from 'react'
import { Avatar, Box, Checkbox, Grid } from '@mui/material'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import Section from 'components/common/Section/Section'
import CustomText from 'components/common/CustomText/CustomText'
import { colors } from 'styles/colors'
import { useProductContext } from 'context/ProductContext'
import PencilIcon from 'icons/PencilIcon'
import SaveIcon from 'icons/SaveIcon'
import XIcon from 'icons/XIcon'
import { useSetProductDisplayImages } from 'hooks/useProduct'
import { snackbarService } from 'services/snackbarService'
import { IProductMediaFile } from 'types/types'

interface Props {
  shared: any;
}

// Which of the product's photos a shopper is shown. The strip holds everything
// that arrived with the product - back panels, barcode close-ups, the odd shelf
// shot - so this is the same pick as the avatar's, asked of the whole strip
// rather than of one photo.
function ProductDisplayImagesDetails({ shared }: Props) {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { product, refetch: refetchProduct } = useProductContext();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const {
    setCarouselImages,
    setCarouselIndex,
    setCarouselOpen,
  } = shared;

  const images: IProductMediaFile[] = product.mediaFiles ?? [];

  useEffect(() => {
    setSelectedIds(
      images.filter((image) => image.isDisplay && image.id).map((image) => String(image.id)),
    );
  }, [product.mediaFiles]);

  const { mutate: setDisplayImages, isLoading } = useSetProductDisplayImages(
    () => {
      setIsEditing(false);
      snackbarService.showSnackbar('Product display images have been updated successfully', 'success');
      refetchProduct();
    },
    (err: any) => {
      snackbarService.showSnackbar('Could not update the display images', 'error');
    },
  )

  const toggle = (mediaId: string) => {
    setSelectedIds((previous) =>
      previous.includes(mediaId)
        ? previous.filter((id) => id !== mediaId)
        : [...previous, mediaId],
    );
  }

  const handleSave = () => {
    setDisplayImages({ productId: product.id, mediaIds: selectedIds });
  }

  const handleCancel = () => {
    setSelectedIds(
      images.filter((image) => image.isDisplay && image.id).map((image) => String(image.id)),
    );
    setIsEditing(false);
  }

  const handleTileClick = (image: IProductMediaFile, index: number) => {
    if (isEditing) {
      if (image.id) toggle(String(image.id));
      return;
    }

    setCarouselImages(images);
    setCarouselIndex(index);
    setCarouselOpen(true);
  }

  return (
    <Box mt={3}>
      <Section
        title="Product Display Images"
        buttons={[
          ...(isEditing
            ? [
              {
                icon: <SaveIcon size={20} color={colors.success} />,
                title: 'Save',
                onClick: () => handleSave(),
                loading: isLoading,
              },
              {
                icon: <XIcon size={15} color={colors.error} />,
                title: 'Cancel',
                onClick: () => handleCancel(),
                disabled: isLoading,
              },
            ]
            : [
              {
                icon: <PencilIcon size={20} color={colors.primary} />,
                title: 'Edit',
                onClick: () => setIsEditing(true),
                disabled: isLoading || images.length === 0,
              }
            ]),
        ]}
        content={
          <Grid size={{ xs: 12 }}>
            {images.length === 0 ? (
              <CustomText
                text={'No photos yet - add some in Product Images, then pick the ones to show.'}
                fontSize={14}
                color={colors.gray}
              />
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {images.map((image, index) => {
                  const isSelected = !!image.id && selectedIds.includes(String(image.id));

                  return (
                    <Box
                      key={image.id ?? index}
                      onClick={() => handleTileClick(image, index)}
                      sx={{
                        position: 'relative',
                        borderRadius: 2,
                        cursor: 'pointer',
                        // The lighter blue of the palette's own controls, so a
                        // ticked photo reads as chosen rather than as flagged.
                        border: `3px solid ${isSelected ? colors.edit : 'transparent'}`,
                      }}
                    >
                      <Avatar
                        src={image.medium || image.large || ''}
                        variant="rounded"
                        sx={{ width: 100, height: 100 }}
                      />

                      {/* Only while the pick is being made. Outside it the
                          border already says which photos are shown, and a row
                          of dead checkboxes reads as something to click. */}
                      {isEditing && (
                      <Checkbox
                        size="small"
                        // Round rather than square: it sits beside the avatar's
                        // picker on the same photos, and the two reading as one
                        // kind of control matters more here than the square's
                        // usual promise that more than one can be ticked.
                        icon={<RadioButtonUncheckedIcon sx={{ fontSize: 20 }} />}
                        checkedIcon={<CheckCircleIcon sx={{ fontSize: 20 }} />}
                        checked={isSelected}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (image.id) toggle(String(image.id));
                        }}
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          padding: 0.25,
                          backgroundColor: 'rgba(255,255,255,0.9)',
                          '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                        }}
                      />
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Grid>
        }
      />
    </Box>
  )
}

export default memo(ProductDisplayImagesDetails)
